import * as React from 'react';
import { ArrowRight, Check, Info, Plus, Send } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import {
  Callout,
  SegmentedControl,
  WorkspaceButton,
  linkishClass,
  useToast,
  workspaceInputClass,
} from '@/components/ui/workspace';
import { ApiError } from '@/lib/api/client';
import type { AreaCoverage } from '@/lib/api/discovery';
import { useAreaFindingsQuery } from '@/lib/api/discovery-findings';
import { useProposeStepQuestions } from '@/lib/api/process-maps';
import { cx } from '@/lib/utils/cn';
import { CoverageStatusChip } from '../coverage-bits';
import { useDiscoveryNavigate } from '../discovery-layout';
import { nextStepText, plural } from '../discovery-logic';
import { AreaPanes } from './area-panes';
import { DocumentEvidenceForm } from './document-evidence-form';
import { SourceChip } from './evidence-bits';
import { areaStatusLine, mapStateText } from './findings-logic';
import { FollowUpForm } from './follow-up-form';
import { PainPointDetail, PainPointList, defaultPainPointId } from './pain-points';
import { defaultStepKey, quoteSupport } from './process-steps';
import { ProcessStepDetail, ProcessStepList, StepsBar, useAreaProcess } from './process-view';
import { QuotesView } from './quotes-view';

/**
 * One area (T-0188, E12-F07; redesigned 5 Nov 2026). Three zones, top to
 * bottom: where the area stands and what to do next (pinned, so it never
 * scrolls away), one control that switches the same list between three
 * ways of reading it, and the list with the detail of the selected row
 * beside it.
 *
 *   Process      the steps of the area's process in order, each with the
 *                quote that describes it and where the quote comes from;
 *                a step with no quote says so and where it came from;
 *   Pain points  what hurts, each with its severity on the row and the
 *                quotes it rests on beside it;
 *   Quotes       what people said, grouped by who said it.
 *
 * Every count is the server's or a length of what it sent. The address
 * holds the view (`?view=`), so a view can be shared.
 */

export const AREA_VIEWS = ['process', 'pain-points', 'quotes'] as const;
export type AreaView = (typeof AREA_VIEWS)[number];

export function parseAreaView(v: string | undefined): AreaView | null {
  return AREA_VIEWS.find((x) => x === v) ?? null;
}

export function AreaFindingsView({
  engagementId,
  areaId,
  areas,
  highlight,
  nothingYet,
  view,
  onViewChange,
}: {
  engagementId: string;
  areaId: string;
  areas: ReadonlyArray<AreaCoverage>;
  /** An evidence id to bring into view, from the ledger. */
  highlight: string | null;
  /** Nothing confirmed or waiting anywhere in the engagement yet. */
  nothingYet: boolean;
  /** The view the address names; null opens the one the area has most to show in. */
  view: AreaView | null;
  onViewChange: (view: AreaView) => void;
}) {
  // After "Look again", ask for a minute while AI reads (Decision 54.23).
  const [polling, setPolling] = React.useState(false);
  React.useEffect(() => {
    if (!polling) return;
    const t = setTimeout(() => setPolling(false), 60_000);
    return () => clearTimeout(t);
  }, [polling]);
  const q = useAreaFindingsQuery(engagementId, areaId, polling);
  const go = useDiscoveryNavigate(engagementId);
  // After "Suggest a question", read the map again for a while (T-0286).
  const [suggesting, setSuggesting] = React.useState(false);
  React.useEffect(() => {
    if (!suggesting) return;
    const t = setTimeout(() => setSuggesting(false), 45_000);
    return () => clearTimeout(t);
  }, [suggesting]);
  const proc = useAreaProcess(
    engagementId,
    (q.data?.processes ?? []).map((p) => p.process_id),
    suggesting,
  );
  const propose = useProposeStepQuestions(engagementId);
  const { toast } = useToast();
  // Every step nobody has described has its question: nothing more to wait for.
  const undescribed = proc.rows.filter((r) => !r.described);
  const allAsked = undescribed.length > 0 && undescribed.every((r) => r.gap_question !== null);
  React.useEffect(() => {
    if (suggesting && allAsked) setSuggesting(false);
  }, [suggesting, allAsked]);
  const [source, setSource] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<'follow-up' | 'evidence' | null>(null);
  // The AI question the follow-up form starts with: the one of the step it was opened from.
  const [pickAi, setPickAi] = React.useState<string | null>(null);
  const [showDone, setShowDone] = React.useState(false);
  const [stepKey, setStepKey] = React.useState<string | null>(null);
  const [painId, setPainId] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);

  // Opened from the ledger: bring that item into view once.
  const scrolled = React.useRef(false);
  React.useEffect(() => {
    if (!highlight || !q.data || scrolled.current) return;
    scrolled.current = true;
    document.getElementById(`evidence-${highlight}`)?.scrollIntoView?.({ block: 'center' });
  }, [highlight, q.data]);

  if (q.error) {
    return (
      <div className="flex flex-col items-center gap-3 p-8">
        <ErrorMessage error={q.error} />
        <WorkspaceButton onClick={() => void q.refetch()}>Try again</WorkspaceButton>
      </div>
    );
  }
  if (!q.data) {
    return (
      <div className="flex items-center justify-center gap-2 p-8 text-body-sm text-ink-500">
        <Spinner /> Loading the area…
      </div>
    );
  }

  const f = q.data;
  const names = new Map(f.sources.map((s) => [s.key, s.name]));
  const next = nextStepText(f.next_step, names);
  const nextStep = f.next_step;
  const openInterview =
    (nextStep.kind === 'review' || nextStep.kind === 'scheduled_interview') && nextStep.interview_id
      ? { id: nextStep.interview_id, code: nextStep.interview_code ?? 'the interview' }
      : null;
  const covered = f.coverage.status === 'corroborated';
  const selectedSource = source && f.sources.some((s) => s.key === source) ? source : null;
  const confirmed = f.said.filter((e) => e.status === 'confirmed');
  const active = f.pain_points.filter((p) => p.status !== 'dismissed');
  const toDecide = active.filter((p) => p.status === 'proposed').length;
  const hasProcess = f.processes.length > 0;
  const shown: AreaView =
    (view === 'process' && !hasProcess ? null : view) ??
    (highlight ? 'quotes' : hasProcess ? 'process' : active.length ? 'pain-points' : 'quotes');

  const step =
    proc.rows.find((r) => r.key === stepKey) ??
    proc.rows.find((r) => r.key === defaultStepKey(proc.rows)) ??
    null;
  const pain =
    active.find((p) => p.id === painId) ??
    active.find((p) => p.id === defaultPainPointId(f.pain_points)) ??
    null;
  const openMap = () => go({ to: 'maps', process: proc.process?.process_id });
  const openForm = (kind: 'follow-up' | 'evidence', aiProposalId?: string) => {
    setAdding(false);
    setPickAi(aiProposalId ?? null);
    setForm(kind);
  };
  const mapId = proc.map?.map.id ?? null;
  const suggest = mapId
    ? () =>
        propose.mutate(mapId, {
          onSuccess: () => setSuggesting(true),
          onError: (e) =>
            toast({
              message: e instanceof ApiError ? e.message : 'Could not ask AI for the questions.',
              tone: 'critical',
            }),
        })
    : undefined;
  const formNode =
    form === 'follow-up' ? (
      <FollowUpForm
        key={pickAi ?? 'area'}
        engagementId={engagementId}
        areaId={areaId}
        areaLabel={f.area.label}
        pickAi={pickAi}
        onDone={() => setForm(null)}
      />
    ) : form === 'evidence' ? (
      <DocumentEvidenceForm
        engagementId={engagementId}
        areaId={areaId}
        areas={areas}
        onDone={() => setForm(null)}
      />
    ) : null;

  const count = (on: boolean) => cx('font-mono font-normal', on ? 'text-ink-200' : 'text-ink-500');
  const todo = (on: boolean) =>
    cx('font-normal', on ? 'text-ink-200' : 'font-semibold text-accent-amber-700');
  const label = (v: AreaView, text: string, n: React.ReactNode, wait?: string | null) => (
    <span className="inline-flex items-baseline gap-1.5">
      {text}
      {n !== null ? (
        <>
          {' '}
          <span className={count(shown === v)}>{n}</span>
        </>
      ) : null}
      {wait ? (
        <>
          {' '}
          <span className={todo(shown === v)}>{wait}</span>
        </>
      ) : null}
    </span>
  );
  const views = [
    ...(hasProcess
      ? [
          {
            value: 'process' as const,
            label: label(
              'process',
              'Process',
              proc.rows.length ? plural(proc.rows.length, 'step') : null,
            ),
          },
        ]
      : []),
    {
      value: 'pain-points' as const,
      label: label(
        'pain-points',
        'Pain points',
        active.length,
        toDecide ? `${toDecide} to decide` : null,
      ),
    },
    {
      value: 'quotes' as const,
      label: label(
        'quotes',
        'Quotes',
        confirmed.length,
        f.waiting.count ? `${f.waiting.count} to review` : null,
      ),
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex-none border-b border-ink-100 bg-paper-0 px-5 pb-3 pt-4">
        {nothingYet ? (
          <Callout
            tone="tip"
            icon={Info}
            className="mb-3"
            actions={
              <WorkspaceButton onClick={() => go({ to: 'sessions' })}>
                Go to interviews
              </WorkspaceButton>
            }
          >
            Findings fill in as you confirm evidence from interviews, questionnaires and documents.
            Nothing is here yet.
          </Callout>
        ) : null}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="min-w-0">
            <h2 className="text-h2 font-semibold text-ink-900">{f.area.label}</h2>
            <p className="text-meta text-ink-500">{f.area.name}</p>
          </div>
          <CoverageStatusChip status={f.coverage.status} label={areaStatusLine(f.coverage)} />
          <span className="flex-1" />
          {f.area.definition ? (
            <button
              type="button"
              className={linkishClass()}
              aria-expanded={showDone}
              onClick={() => setShowDone((v) => !v)}
            >
              <Info className="h-3 w-3" aria-hidden="true" />{' '}
              {showDone ? 'Hide' : 'What “done” looks like'}
            </button>
          ) : null}
          <WorkspaceButton onClick={() => openForm('evidence')}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add evidence from a document
          </WorkspaceButton>
          {!covered ? (
            <WorkspaceButton variant="primary" onClick={() => openForm('follow-up')}>
              <Send className="h-3.5 w-3.5" aria-hidden="true" /> Ask someone a follow-up
            </WorkspaceButton>
          ) : null}
        </div>
        {showDone && f.area.definition ? (
          <p className="mt-2.5 rounded-card bg-paper-50 px-3 py-2.5 text-body-sm text-ink-700">
            {f.area.definition}
          </p>
        ) : null}
        <section aria-label="Where this area stands" className="mt-3 flex flex-col gap-2.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-500">
              Sources {f.coverage.sources} of {f.coverage.required}
            </span>
            {f.sources.length ? (
              f.sources.map((s) => (
                <SourceChip
                  key={s.key}
                  source={s}
                  on={selectedSource === s.key}
                  onToggle={() => {
                    const on = selectedSource === s.key;
                    setSource(on ? null : s.key);
                    // A source's quotes are read in the Quotes view.
                    if (!on && shown !== 'quotes') onViewChange('quotes');
                  }}
                />
              ))
            ) : (
              <span className="text-meta text-ink-500">None yet</span>
            )}
          </div>
          <Callout
            tone={next.done ? 'ok' : 'ask'}
            icon={next.done ? Check : ArrowRight}
            actions={
              openInterview ? (
                <WorkspaceButton
                  variant="primary"
                  onClick={() => go({ to: 'sessions', interview: openInterview.id })}
                >
                  Open {openInterview.code}
                </WorkspaceButton>
              ) : undefined
            }
          >
            {next.text}
          </Callout>
        </section>
      </header>

      <div className="flex flex-none flex-wrap items-center gap-x-4 gap-y-2 border-b border-ink-100 bg-paper-0 px-5 py-2">
        <SegmentedControl
          aria-label="View"
          options={views}
          value={shown}
          onChange={(v: AreaView) => {
            setForm(null);
            setAdding(false);
            onViewChange(v);
          }}
        />
        {shown === 'process' && proc.process ? (
          <span className="ml-auto flex flex-wrap items-center gap-2.5 text-meta text-ink-500">
            {proc.rows.length ? (
              <>
                <StepsBar rows={proc.rows} />
                <span className="font-semibold text-ink-700">
                  {proc.coverage.described} of {plural(proc.coverage.total, 'step')} described
                </span>
              </>
            ) : null}
            {proc.processes.length > 1 ? (
              <select
                aria-label="Process"
                className={cx(workspaceInputClass, 'h-7 w-auto py-0 text-meta')}
                value={proc.process.process_id}
                onChange={(e) => {
                  setStepKey(null);
                  proc.select(e.target.value);
                }}
              >
                {proc.processes.map((p) => (
                  <option key={p.process_id} value={p.process_id}>
                    {p.name}
                  </option>
                ))}
              </select>
            ) : (
              <span>{proc.process.name}</span>
            )}
            {proc.map ? null : (
              <span>
                ·{' '}
                {mapStateText(
                  f.processes.find((p) => p.process_id === proc.process?.process_id)?.map_state ??
                    'waiting',
                )}
              </span>
            )}
            <button type="button" className={linkishClass()} onClick={openMap}>
              Open the map
            </button>
          </span>
        ) : null}
      </div>

      {shown === 'process' ? (
        <AreaPanes
          label="Process today"
          list={
            <ProcessStepList
              process={proc}
              said={confirmed}
              painPoints={f.pain_points}
              selectedKey={form ? null : (step?.key ?? null)}
              onSelect={(key) => {
                setForm(null);
                setStepKey(key);
              }}
              onAsk={(row) => {
                setStepKey(row.key);
                openForm(covered ? 'evidence' : 'follow-up', row.gap_question?.ai_proposal_id);
              }}
              onOpenMap={openMap}
            />
          }
          detail={
            formNode ??
            (proc.rows.length ? (
              <ProcessStepDetail
                row={step}
                said={confirmed}
                painPoints={f.pain_points}
                onAskFollowUp={covered ? undefined : (ai) => openForm('follow-up', ai)}
                onSuggest={suggest}
                suggesting={suggesting || propose.isPending}
                onAddDocument={() => openForm('evidence')}
                onOpenMap={openMap}
                onOpenPainPoint={(id) => {
                  setPainId(id);
                  onViewChange('pain-points');
                }}
              />
            ) : null)
          }
        />
      ) : shown === 'pain-points' ? (
        <AreaPanes
          label="Pain points"
          list={
            <PainPointList
              engagementId={engagementId}
              areaLabel={f.area.label}
              painPoints={f.pain_points}
              selectedId={form ? null : (pain?.id ?? null)}
              adding={adding}
              onSelect={(id) => {
                setForm(null);
                setAdding(false);
                setPainId(id);
              }}
              onAdd={() => {
                setForm(null);
                setAdding(true);
              }}
              onLookAgainStarted={() => setPolling(true)}
            />
          }
          detail={
            formNode ?? (
              <PainPointDetail
                engagementId={engagementId}
                areaId={areaId}
                areaLabel={f.area.label}
                p={pain}
                confirmed={confirmed}
                adding={adding}
                steps={
                  pain ? proc.rows.filter((r) => r.pain_points.some((x) => x.id === pain.id)) : []
                }
                onAdded={(id) => {
                  setAdding(false);
                  if (id) setPainId(id);
                }}
                onOpenStep={(key) => {
                  setStepKey(key);
                  onViewChange('process');
                }}
              />
            )
          }
        />
      ) : (
        <AreaPanes
          label="What people said"
          list={
            <>
              {formNode ? <div className="max-w-[980px] px-5 pt-4">{formNode}</div> : null}
              <QuotesView
                engagementId={engagementId}
                areaId={areaId}
                said={f.said}
                sources={f.sources}
                waiting={f.waiting}
                source={selectedSource}
                onShowEveryone={() => setSource(null)}
                highlight={highlight}
                support={quoteSupport(proc.rows, f.pain_points)}
                onReview={(review) => go({ to: 'sessions', review })}
              />
            </>
          }
          detail={null}
        />
      )}
    </div>
  );
}
